import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";

import Toast from '../../components/Toast/Toast';
import { updateCategoryAPI } from '../../utils/api';
import { BASE_URL } from '../../utils/configConstants';
import Input from '../../components/Input/Input';
import "./EditCategory.css";

const EditCategory = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  // Get category data from navigation state
  const category = location.state?.category;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Form state
  const [categoryName, setCategoryName] = useState("");
  const [categoryImage, setCategoryImage] = useState(null);
  const [existingImage, setExistingImage] = useState(null);
  const [removeExistingImage, setRemoveExistingImage] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const [sizeChart, setSizeChart] = useState([
    { name: "", values: [""], index: [0] }
  ]);
  const [sizeChartRows, setSizeChartRows] = useState([""]);

  const showToast = (message, type = "error") => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: "", type: "" }), 2500);
  };

  // Populate form with category data when component mounts
  useEffect(() => {
    if (category) {
      setCategoryName(category?.name || "");
      setExistingImage(category?.image || null);
      setRemoveExistingImage(false);
      setLoading(false);

      // Set up size chart data
      if (category?.size_chart && category?.size_chart.length > 0) {
        const loadedSizeChart = category.size_chart.map(column => ({
          _id: column._id || "",
          name: column.name || "",
          values: column.rows ? column.rows.map(row => row.name || "") : [],
          index: column.rows ? column.rows.map((row, idx) => idx) : []
        }));

        // Get the maximum number of rows to determine sizeChartRows length
        const maxRows = Math.max(...loadedSizeChart.map(col => col.values.length));
        setSizeChart(loadedSizeChart);
        setSizeChartRows(Array(maxRows).fill(""));
      } else {
        // Initialize with empty size chart if no data
        setSizeChart([{ name: "", values: [""], index: [0] }]);
        setSizeChartRows([""]);
      }
    } else {
      setError("Category data not found. Please go back and try again.");
      setLoading(false);
    }
  }, [category]);

  // Show loading state
  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        Loading category...
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '50px', color: 'red' }}>
        {error}
      </div>
    );
  }

  // Show message if no category found
  if (!category) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        Category not found
      </div>
    );
  }

  // Convert file to base64
  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader?.result);
      reader.onerror = error => reject(error);
    });
  };

  // Handle image change
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpg', 'image/jpeg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      showToast("Invalid file format. Only PNG, JPG, JPEG, and WebP files are allowed.", "error");
      e.target.value = '';
      return;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      showToast(`File too large: ${file.name}. Maximum file size is 5MB.`, "error");
      e.target.value = '';
      return;
    }

    setCategoryImage(file);
    setRemoveExistingImage(true);
  };

  // Remove new image
  const handleRemoveImage = () => {
    setCategoryImage(null);
    // Reset image input
    const imageInput = document.getElementById('category-image-input');
    if (imageInput) imageInput.value = '';
  };

  // Remove existing image
  const handleRemoveExistingImage = () => {
    setRemoveExistingImage(true);
    setExistingImage(null);
  };

  // Size Chart Handlers
  const handleSizeChartColumnChange = (idx, value) => {
    const updated = sizeChart.map((column, i) =>
      i === idx ? { ...column, name: value } : column
    );
    setSizeChart(updated);
  };

  const handleAddMoreSizeChartColumn = () => {
    const newColumn = {
      name: "",
      values: sizeChartRows.map((_, rowIndex) => ""),
      index: sizeChartRows.map((_, rowIndex) => rowIndex)
    };
    setSizeChart([...sizeChart, newColumn]);
  };

  const handleRemoveSizeChartColumn = (idx) => {
    if (sizeChart.length > 1) {
      const updated = sizeChart.filter((_, i) => i !== idx);
      setSizeChart(updated);
    }
  };

  const handleSizeChartRowChange = (rowIdx, colIdx, value) => {
    const updated = sizeChart.map((column, i) => {
      if (i === colIdx) {
        const newValues = [...column.values];
        newValues[rowIdx] = value;
        return { ...column, values: newValues };
      }
      return column;
    });
    setSizeChart(updated);
  };

  const handleAddMoreSizeChartRow = () => {
    const newRow = sizeChart.map((column) => "");
    setSizeChartRows([...sizeChartRows, ""]);

    const updated = sizeChart.map((column) => ({
      ...column,
      values: [...column.values, ""],
      index: [...column.index, sizeChartRows.length]
    }));
    setSizeChart(updated);
  };

  const handleRemoveSizeChartRow = (rowIdx) => {
    if (sizeChartRows.length > 1) {
      setSizeChartRows(sizeChartRows.filter((_, i) => i !== rowIdx));

      const updated = sizeChart.map((column) => ({
        ...column,
        values: column.values.filter((_, i) => i !== rowIdx),
        index: column.index.filter((_, i) => i !== rowIdx)
      }));
      setSizeChart(updated);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Set loading state
    setSubmitLoading(true);

    // Validation checks
    if (!categoryName.trim()) {
      showToast("Please enter a category name.");
      setSubmitLoading(false);
      return;
    }

    if (!categoryImage && !existingImage && removeExistingImage) {
      showToast("Please select a category image.");
      setSubmitLoading(false);
      return;
    }

    // Size chart validation - check for empty values in any row
    const sizeChartColumns = sizeChart.filter(column => column.name && column.name.trim() !== "");
    if (sizeChartColumns.length > 0) {
      // Check if any column exists but has no values in raw data
      for (let colIdx = 0; colIdx < sizeChartColumns.length; colIdx++) {
        const column = sizeChartColumns[colIdx];
        const hasAnyValue = column.values.some(value => value && value.trim() !== "");
        
        if (!hasAnyValue) {
          showToast(`Column "${column.name}" exists but has no values. Please add values or remove the column.`);
          setSubmitLoading(false);
          return;
        }
      }

      // Check for empty values in any row
      for (let rowIdx = 0; rowIdx < sizeChartRows.length; rowIdx++) {
        const hasEmptyValue = sizeChartColumns.some(column => {
          const value = column.values[rowIdx] || "";
          return value.trim() === "";
        });

        if (hasEmptyValue) {
          showToast(`Please fill all values in row ${rowIdx + 1} of the size chart.`);
          setSubmitLoading(false);
          return;
        }
      }
    }

    try {
      // Prepare category data as FormData
      const formData = new FormData();
      formData.append('category_id', id);
      formData.append('name', categoryName);

      // Append image if new one is uploaded
      if (categoryImage) {
        formData.append('image', categoryImage); // Send as file directly
      }

      // If existing image should be removed
      if (removeExistingImage && !categoryImage) {
        formData.append('remove_image', 1);
      }

      // Append size chart as array
      const sizeChartData = sizeChart.filter(column => column.name && column.values.some(val => val.trim() !== ""));
      if (sizeChartData.length > 0) {
        sizeChartData.forEach((column) => {
          // Create rows array with _id, name, and row_index for each value
          const rows = [];
          column.values.forEach((value, idx) => {
            if (value.trim() !== "") {
              // For existing data, we need to find corresponding row _id and row_index from original data
              let rowId = "";
              let rowIndex = idx;

              if (category?.size_chart) {
                const originalColumn = category.size_chart.find(col => col._id === column._id);
                if (originalColumn?.rows && originalColumn.rows[idx]) {
                  rowId = originalColumn.rows[idx]._id || "";
                  rowIndex = originalColumn.rows[idx].row_index || idx;
                }
              }

              rows.push({
                _id: rowId,
                name: value,
                row_index: rowIndex
              });
            }
          });
          formData.append('size_chart[]', JSON.stringify({
            _id: column._id || "",
            name: column.name,
            rows: rows
          }));
        });
      }
      
      const response = await updateCategoryAPI(formData);

      if (response?.status === 200) {
        showToast("Category updated successfully!", "success");

        setTimeout(() => {
          navigate("/categories?refresh=true");
        }, 1000);
      } else if (response?.message) {
        showToast(typeof response?.message === 'string'
          ? response?.message
          : 'Failed due to internal error', "error");
      } else {
        showToast('Failed due to internal error', "error");
      }
    } catch (err) {
      showToast('Failed due to internal error', "error");
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <>
      <Toast toast={toast} />
      <div className="category-main-content">
        <div className="order-details-header">
          <button className="back-button" onClick={() => {
            navigate("/categories");
          }}>
            <FaArrowLeft /> Back to Categories
          </button>
        </div>
        <form onSubmit={handleSubmit} className="category-form">
          <Input
            title="Category Name"
            value={categoryName}
            onChange={e => setCategoryName(e.target.value)}
            placeholder="Enter category name"
            required
            maxLength={100}
          />

          <div className="image-upload-section">
            <label htmlFor="category-image-input" className="input-label">
              Category Image <span className="required-asterisk">*</span>
            </label>
            <div className="image-upload-container">
              {categoryImage ? (
                <div className="cat-image-preview">
                  <img
                    src={URL.createObjectURL(categoryImage)}
                    alt="Category Preview"
                    className="preview-image"
                  />
                  <button
                    type="button"
                    className="remove-image-btn"
                    onClick={handleRemoveImage}
                  >
                    ×
                  </button>
                </div>
              ) : existingImage && !removeExistingImage ? (
                <div className="cat-image-preview">
                  <img
                    src={BASE_URL + existingImage}
                    alt="Existing category"
                    className="preview-image"
                  />
                  <button
                    type="button"
                    className="remove-image-btn"
                    onClick={handleRemoveExistingImage}
                  >
                    ×
                  </button>
                </div>
              ) : (
                <label htmlFor="category-image-input" className="upload-placeholder">
                  <div className="upload-icon">📷</div>
                  <p>Click to upload image</p>
                </label>
              )}
              <input
                id="category-image-input"
                type="file"
                accept="image/png,image/jpg,image/jpeg,image/webp"
                onChange={handleImageChange}
                className="image-input"
              />
            </div>
            <p className="upload-hint">
              Accepted: PNG, JPG, JPEG, WEBP. Max size: 5MB. Recommended: 1:1 aspect ratio.
            </p>
          </div>

          {/* Size Chart Section */}
          <div className="size-section">
            <h3 className="size-title">Size Chart</h3>

            {/* Column Headers */}
            <div className="size-header" style={{ marginBottom: '1rem', display: 'flex', flexDirection: 'column' }}>
              {sizeChart.map((column, colIdx) => (
                <div key={colIdx} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <input
                    type="text"
                    placeholder="Column name (e.g., Size, Bust, Waist)"
                    value={column.name}
                    onChange={(e) => handleSizeChartColumnChange(colIdx, e.target.value)}
                    className="size-textarea"
                    style={{ width: '300px', maxWidth: '400px' }}
                    maxLength={255}
                  />
                  {sizeChart.length > 1 && (
                    <button
                      type="button"
                      className="remove-price-btn"
                      onClick={() => handleRemoveSizeChartColumn(colIdx)}
                      title="Delete Column"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                        strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6"></polyline>
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"></path>
                        <line x1="10" y1="11" x2="10" y2="17"></line>
                        <line x1="14" y1="11" x2="14" y2="17"></line>
                      </svg>
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* Add More Column Button */}
            <div className="add-size-btn-container" style={{ marginBottom: '1rem' }}>
              <button
                type="button"
                onClick={handleAddMoreSizeChartColumn}
                className="add-price-btn"
                title="Add Column"
              >
                Add More
              </button>
            </div>

            {/* Rows - show if there's at least one column */}
            {sizeChart.length > 0 && (
              <div style={{ marginBottom: '1rem' }}>
                {sizeChartRows.map((_, rowIdx) => (
                  <div key={rowIdx} className="price-row" style={{ marginBottom: '0.5rem' }}>
                    {sizeChart.map((column, colIdx) => (
                      <input
                        key={colIdx}
                        type="text"
                        placeholder={`Row ${rowIdx + 1} value`}
                        value={column.values[rowIdx] || ""}
                        onChange={(e) => handleSizeChartRowChange(rowIdx, colIdx, e.target.value)}
                        className="size-textarea"
                        style={{ flex: 1, marginRight: '0.5rem' }}
                        maxLength={255}
                      />
                    ))}
                    {sizeChartRows.length > 1 && (
                      <button
                        type="button"
                        className="remove-price-btn"
                        onClick={() => handleRemoveSizeChartRow(rowIdx)}
                        title="Delete Row"
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                          strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="3 6 5 6 21 6"></polyline>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"></path>
                          <line x1="10" y1="11" x2="10" y2="17"></line>
                          <line x1="14" y1="11" x2="14" y2="17"></line>
                        </svg>
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Add More Row Button - show if there's at least one column */}
            {sizeChart.length > 0 && (
              <div className="add-size-btn-container">
                <button
                  type="button"
                  onClick={handleAddMoreSizeChartRow}
                  className="add-price-btn"
                  title="Add Row"
                >
                  Add More
                </button>
              </div>
            )}
          </div>

          <div className="submit-btn-row">
            <button
              type="submit"
              className="submit-btn"
              disabled={submitLoading}
            >
              {submitLoading ? "Updating..." : "Update"}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default EditCategory;
